import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';
import { CanvasPoint, Viewport, ViewportBridge, ViewportProvider, } from './lib';
import { getOuterGroundColor, GroundType } from '../../models/ground';
import { GroundGeometry } from '../../types/webPlayer';
import Pitch from './components/Pitch';
import ZoomControls from '../webPlayerGbf/ZoomControls';
import { getRectangleDimensions } from './lib/ground';
import { usePlayerStageContext } from '../webPlayerGbf/VideoPlayerContext';
import { isRectangle } from '../../lib/geometry';
import { GROUND_MAX_ANGLE_TOLERANCE } from '../constants';
import { useTranslation } from 'react-i18next';

export interface CenterVertices {
  top: {
    x: number; y: number;
  },
  bottom: {
    x: number; y: number;
  }
}

const VectorGround = ( props: {
  centerVertices: CenterVertices;
  corners: {
    A: CanvasPoint,
    B: CanvasPoint,
    C: CanvasPoint,
    D: CanvasPoint,
  }
  vp: Viewport;
  children?: React.ReactNode;
  overlayChildren?: React.ReactNode;
  groundDetails?: GroundType;
  groundGeometry?: GroundGeometry;
  ground: {
    coordType: 'local' | 'gps';
    vertices: [
      { x: number; y: number },
      { x: number; y: number },
      { x: number; y: number },
      { x: number; y: number },
    ];
  };
  hideControls?: boolean;
  players: {
    id: number;
    name: string;
    data: {
      timestamp: number;
      x: number;
      y: number;
    }[];
  }[],
  scaleReference: number,
  vertices?: boolean
} ) => {
  const {
    children,
    overlayChildren,
    ground: groundData,
    hideControls,
    players: playersData,
    groundDetails,
    groundGeometry,
    centerVertices,
    corners,
    scaleReference,
    vp,
    vertices,
  } = props;

  const boxRef = useRef<HTMLDivElement | undefined>(undefined);
  const playerStageRef = usePlayerStageContext();
  const { t } = useTranslation();

  const [zoom, setZoom] = useState(0.7);

  const {width, height} = groundGeometry?.vertex
    ? getRectangleDimensions(
      groundGeometry.vertex.vertexA,
      groundGeometry.vertex.vertexB,
      groundGeometry.vertex.vertexC,
      groundGeometry.vertex.vertexD,
    )
    : {width: 0, height: 0};

  const [stagePos, setStagePos] = useState({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (stagePos.x === 0 && stagePos.y === 0 && width > 2 && height > 2 && vp.w > 2 && vp.h > 2) {
      setStagePos({
        x: (vp.w - vp.w * zoom) / 2,
        y: (vp.h - vp.h * zoom) / 2,
      });
    }
  }, [vp, stagePos]);

  const handleSetZoom = (newZoom: number) => {
    const centerX = vp.w / 2;
    const centerY = vp.h / 2;
    setStagePos((prev) => ({
      x: centerX - (centerX - prev.x) * (newZoom / zoom),
      y: centerY - (centerY - prev.y) * (newZoom / zoom),
    }));
    setZoom(newZoom);
  };

  const handleDragEnd = (e: any) => {
    if (!hideControls) {
      setStagePos({ x: e.target.x(), y: e.target.y() });
    }
  };

  const isValidRectangle = useMemo(() => {
    return groundGeometry?.vertex.vertexA
      && groundGeometry?.vertex.vertexB
      && groundGeometry?.vertex.vertexC
      && groundGeometry?.vertex.vertexD
      && isRectangle([
      {x: groundGeometry?.vertex.vertexA.x, y: groundGeometry?.vertex.vertexA.y},
      {x: groundGeometry?.vertex.vertexB.x, y: groundGeometry?.vertex.vertexB.y},
      {x: groundGeometry?.vertex.vertexC.x, y: groundGeometry?.vertex.vertexC.y},
      {x: groundGeometry?.vertex.vertexD.x, y: groundGeometry?.vertex.vertexD.y},
    ], { toleranceDeg: GROUND_MAX_ANGLE_TOLERANCE });
  }, [groundGeometry?.vertex]);

  return (
    <div
      className="ground-box"
      id="ground-box"
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
      // ref={onBoxRefChange}
    >

      <div
        className="ground-field__component"
        style={{
          width: '100%',
        }}
      >
        <div
          className="ground-field__component__outer"
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <div
            className="ground-field__component__inner"
            style={{
              backgroundColor: getOuterGroundColor(groundDetails?.groundSurfaceColor),
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >

            <div
              className="player__wrapper"
              ref={boxRef}
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              {
                vp
                && (
                  <ViewportProvider viewport={vp}>
                    <Stage
                      width={boxRef.current?.offsetWidth || vp.w}
                      height={boxRef.current?.offsetHeight || vp.h}
                      x={stagePos.x}
                      y={stagePos.y}
                      onDragEnd={handleDragEnd}
                      draggable={!hideControls}
                      scaleX={zoom}
                      scaleY={zoom}
                      className="vp__stage"
                      ref={playerStageRef}
                    >
                      <ViewportBridge>
                        <Layer>
                          {
                            isValidRectangle
                            && (
                              <Pitch
                                defaultGround={false}
                                groundData={groundDetails}
                                groundGeometry={{
                                  centerVertices,
                                  center: {
                                    x: (corners.A.cx + corners.C.cx) / 2,
                                    y: (corners.A.cy + corners.C.cy) / 2,
                                  },
                                  vertex: {
                                    vertexA: { x: corners.A.cx, y: corners.A.cy },
                                    vertexB: { x: corners.B.cx, y: corners.B.cy },
                                    vertexC: { x: corners.C.cx, y: corners.C.cy },
                                    vertexD: { x: corners.D.cx, y: corners.D.cy },
                                  },
                                  dims: {
                                    /* @todo verificare le dimensioni */
                                    width: Math.abs(corners.D.cx - corners.A.cx),
                                    height: Math.abs(corners.B.cy - corners.A.cy),
                                  },
                                }}
                                scale={scaleReference}
                                vertices={vertices}
                                showInDrawer={false}
                              />
                            )
                          }
                        </Layer>

                        {children}
                      </ViewportBridge>
                    </Stage>
                  </ViewportProvider>
                )
              }

              {
                !isValidRectangle && (
                  <div className="ground-invalid-coords">
                    {t('ground.error.invalidCoords', 'Invalid coords, please check vertices')}
                  </div>
                )
              }

              {overlayChildren && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: vp.w,
                    height: vp.h,
                    transform: `translate(${stagePos.x}px, ${stagePos.y}px) scale(${zoom})`,
                    transformOrigin: '0 0',
                    pointerEvents: 'none',
                  }}
                >
                  {overlayChildren}
                </div>
              )}

              {
                !hideControls ? <ZoomControls setZoom={handleSetZoom} zoom={zoom} defaultZoom={0.7} /> : null
              }

            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default VectorGround;
